import React, { useState } from 'react';
import api from '../../services/api';

/**
 * Debug component for testing API connectivity
 * This component allows testing various API endpoints to diagnose communication issues
 */
const ApiTester = () => {
  const [pingResult, setPingResult] = useState(null);
  const [echoResult, setEchoResult] = useState(null);
  const [chatResult, setChatResult] = useState(null);
  const [loading, setLoading] = useState({
    ping: false,
    echo: false,
    chat: false
  });
  const [error, setError] = useState({
    ping: null,
    echo: null,
    chat: null
  });

  // Test ping endpoint
  const testPing = async () => {
    setLoading(prev => ({ ...prev, ping: true }));
    setError(prev => ({ ...prev, ping: null }));
    
    try {
      const response = await api.get('/debug/ping');
      console.log('Ping response:', response.data);
      setPingResult(response.data);
    } catch (err) {
      console.error('Ping error:', err);
      setError(prev => ({ 
        ...prev, 
        ping: {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, ping: false }));
    }
  };

  // Test echo endpoint
  const testEcho = async () => {
    setLoading(prev => ({ ...prev, echo: true }));
    setError(prev => ({ ...prev, echo: null }));
    
    const testData = {
      message: "Test message",
      timestamp: new Date().toISOString(),
      testValue: 123
    };
    
    try {
      const response = await api.post('/debug/echo', testData);
      console.log('Echo response:', response.data);
      setEchoResult(response.data);
    } catch (err) {
      console.error('Echo error:', err);
      setError(prev => ({ 
        ...prev, 
        echo: {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, echo: false }));
    }
  };

  // Test chat endpoint
  const testChat = async () => {
    setLoading(prev => ({ ...prev, chat: true }));
    setError(prev => ({ ...prev, chat: null }));
    
    const chatData = {
      question: "Test question for debugging",
      embedding: [],
      top_k: 5,
      user_id: "test-user-id"
    };
    
    try {
      const response = await api.post('/chat/ask', chatData);
      console.log('Chat response:', response.data);
      setChatResult(response.data);
    } catch (err) {
      console.error('Chat error:', err);
      setError(prev => ({ 
        ...prev, 
        chat: {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          config: err.config
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, chat: false }));
    }
  };

  // Format JSON for display
  const formatJson = (data) => {
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className="bg-gray-800 text-white p-6 rounded-lg shadow-lg max-w-4xl mx-auto my-8">
      <h1 className="text-2xl font-bold mb-6 text-blue-400">API Connection Tester</h1>
      
      {/* Ping Test */}
      <div className="mb-8 border border-gray-700 rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-3 text-blue-300">1. Test Basic Connectivity (GET)</h2>
        <button 
          onClick={testPing} 
          disabled={loading.ping}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4 disabled:opacity-50"
        >
          {loading.ping ? 'Testing...' : 'Test GET /debug/ping'}
        </button>
        
        {pingResult && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-green-400">Success:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-green-300">
              {formatJson(pingResult)}
            </pre>
          </div>
        )}
        
        {error.ping && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-red-400">Error:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-red-300">
              {formatJson(error.ping)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Echo Test */}
      <div className="mb-8 border border-gray-700 rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-3 text-blue-300">2. Test POST Request with JSON Body</h2>
        <button 
          onClick={testEcho} 
          disabled={loading.echo}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4 disabled:opacity-50"
        >
          {loading.echo ? 'Testing...' : 'Test POST /debug/echo'}
        </button>
        
        {echoResult && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-green-400">Success:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-green-300">
              {formatJson(echoResult)}
            </pre>
          </div>
        )}
        
        {error.echo && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-red-400">Error:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-red-300">
              {formatJson(error.echo)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Chat Test */}
      <div className="mb-8 border border-gray-700 rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-3 text-blue-300">3. Test Chat API Endpoint</h2>
        <button 
          onClick={testChat} 
          disabled={loading.chat}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4 disabled:opacity-50"
        >
          {loading.chat ? 'Testing...' : 'Test POST /chat/ask'}
        </button>
        
        {chatResult && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-green-400">Success:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-green-300">
              {formatJson(chatResult)}
            </pre>
          </div>
        )}
        
        {error.chat && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-red-400">Error:</h3>
            <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-red-300">
              {formatJson(error.chat)}
            </pre>
          </div>
        )}
      </div>
      
      <div className="mt-6 text-gray-400">
        <p>Use this tool to diagnose API connectivity issues between the frontend and backend.</p>
        <p>Check the browser console for additional debugging information.</p>
      </div>
    </div>
  );
};

export default ApiTester;
